import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subDays, startOfDay, endOfDay, isWithinInterval } from 'date-fns';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import {
  RefreshCw,
  Users,
  Eye,
  Clock,
  MapPin,
  Calendar,
  Activity,
  LogIn,
  LogOut,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import pb from '../lib/pocketbaseClient';

const formatDateTime = (dateStr) =>
  dateStr ? format(new Date(dateStr), 'dd MMM yyyy, hh:mm a') : '—';

const COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899'];

const Analytics = () => {
  const [dateRange, setDateRange] = useState('7days');
  const [refreshKey, setRefreshKey] = useState(0);

  // ─── Fetch page visits ──────────────────────────────────
  const { data: visits = [], isLoading: visitsLoading, refetch: refetchVisits } = useQuery({
    queryKey: ['analyticsVisits', refreshKey],
    queryFn: async () => {
      const records = await pb.collection('page_visits').getFullList({
        sort: '-timestamp',
      });
      return records;
    },
  });

  // ─── Fetch user activity (login/logout) ────────────────
  const { data: activities = [], isLoading: activitiesLoading, refetch: refetchActivities } = useQuery({
    queryKey: ['analyticsActivities', refreshKey],
    queryFn: async () => {
      const records = await pb.collection('user_activity').getFullList({
        sort: '-timestamp',
        expand: 'userId',
      });
      return records;
    },
  });

  // ─── Fetch team members ──────────────────────────────────
  const { data: users = [] } = useQuery({
    queryKey: ['analyticsUsers', refreshKey],
    queryFn: async () => {
      return await pb.collection('users').getFullList({
        filter: 'role != ""',
      });
    },
  });

  const isLoading = visitsLoading || activitiesLoading;

  // ─── Filter by date range ──────────────────────────────
  const getDateFilter = () => {
    const now = new Date();
    switch (dateRange) {
      case 'today': return { start: startOfDay(now), end: endOfDay(now) };
      case 'yesterday': return { start: startOfDay(subDays(now, 1)), end: endOfDay(subDays(now, 1)) };
      case '7days': return { start: subDays(now, 7), end: now };
      case '30days': return { start: subDays(now, 30), end: now };
      default: return { start: subDays(now, 7), end: now };
    }
  };

  const dateFilter = getDateFilter();
  const filteredVisits = visits.filter(v => {
    const d = new Date(v.timestamp);
    return isWithinInterval(d, { start: dateFilter.start, end: dateFilter.end });
  });
  const filteredActivities = activities.filter(a => {
    const d = new Date(a.timestamp);
    return isWithinInterval(d, { start: dateFilter.start, end: dateFilter.end });
  });

  // ─── Compute stats ──────────────────────────────────────
  const totalVisits = filteredVisits.length;
  const uniqueVisitors = new Set(filteredVisits.map(v => v.visitorId || v.ip)).size;
  const visitsToday = filteredVisits.filter(v => format(new Date(v.timestamp), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')).length;

  const loggedInToday = filteredActivities.filter(a => a.type === 'login' && format(new Date(a.timestamp), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')).length;
  const loggedOutToday = filteredActivities.filter(a => a.type === 'logout' && format(new Date(a.timestamp), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')).length;

  const activeNow = users.filter(u => {
    const last = activities.filter(a => a.userId === u.id).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
    return last && last.type === 'login' && new Date() - new Date(last.timestamp) < 15 * 60 * 1000;
  }).length;

  // ─── Page views trend ──────────────────────────────────
  const trendData = useMemo(() => {
    const days = [];
    for (let d = dateFilter.start; d <= dateFilter.end; d = new Date(d.setDate(d.getDate() + 1))) {
      const dateStr = format(d, 'yyyy-MM-dd');
      const count = filteredVisits.filter(v => format(new Date(v.timestamp), 'yyyy-MM-dd') === dateStr).length;
      days.push({ date: format(d, 'dd MMM'), visits: count });
    }
    return days;
  }, [filteredVisits, dateFilter]);

  // ─── Page breakdown ────────────────────────────────────
  const pageData = useMemo(() => {
    const map = {};
    filteredVisits.forEach(v => {
      const page = v.page || '/';
      map[page] = (map[page] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 10);
  }, [filteredVisits]);

  // ─── Recent visits ──────────────────────────────────────
  const recentVisits = filteredVisits.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 20);

  // ─── Team activity timeline ────────────────────────────
  const teamActivity = useMemo(() => {
    const map = {};
    filteredActivities.forEach(a => {
      const userId = a.userId;
      if (!map[userId]) map[userId] = [];
      map[userId].push(a);
    });
    return Object.entries(map).map(([userId, events]) => {
      const user = users.find(u => u.id === userId);
      const sorted = events.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      let totalDuration = 0;
      let lastLogin = null;
      let lastLogout = null;
      for (let i = 0; i < sorted.length; i++) {
        if (sorted[i].type === 'login') {
          lastLogin = sorted[i].timestamp;
        } else if (sorted[i].type === 'logout' && lastLogin) {
          const dur = (new Date(sorted[i].timestamp) - new Date(lastLogin)) / 60000; // minutes
          if (dur > 0 && dur < 1440) totalDuration += dur;
          lastLogin = null;
        }
      }
      return {
        user: user?.name || 'Unknown',
        userId,
        events: sorted,
        totalDuration: Math.round(totalDuration),
        lastSeen: sorted[sorted.length - 1]?.timestamp || null,
      };
    });
  }, [filteredActivities, users]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    refetchVisits();
    refetchActivities();
  };

  if (isLoading && visits.length === 0 && activities.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-slate-50/50 min-h-screen">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex flex-wrap justify-between items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-2">
              <Activity className="w-8 h-8" /> Analytics Dashboard
            </h1>
            <p className="text-white/80">Real‑time insights for your website</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[150px] bg-white/20 text-white border-white/30">
                <SelectValue placeholder="Date Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 hover:text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
        <Card className="border-0 shadow-lg bg-white hover:shadow-xl transition-all">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-indigo-100 rounded-full">
              <Eye className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Visits</p>
              <p className="text-2xl font-bold">{totalVisits}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-white hover:shadow-xl transition-all">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-emerald-100 rounded-full">
              <Users className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Unique Visitors</p>
              <p className="text-2xl font-bold">{uniqueVisitors}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-white hover:shadow-xl transition-all">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-full">
              <Calendar className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Today’s Visits</p>
              <p className="text-2xl font-bold">{visitsToday}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-white hover:shadow-xl transition-all">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-full">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Active Now</p>
              <p className="text-2xl font-bold">{activeNow}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-white hover:shadow-xl transition-all">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-rose-100 rounded-full">
              <LogIn className="w-6 h-6 text-rose-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Login Today</p>
              <p className="text-2xl font-bold">{loggedInToday}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-500" /> Page Views Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="visitGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="visits" stroke="#6366f1" strokeWidth={2} fill="url(#visitGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-500" /> Top Pages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pageData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={60} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for detailed data */}
      <Tabs defaultValue="visits" className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="visits" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg">
            Recent Visits
          </TabsTrigger>
          <TabsTrigger value="team" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg">
            Team Activity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="visits" className="mt-6">
          <Card className="border-0 shadow-lg overflow-hidden">
            <CardHeader>
              <CardTitle>Recent Page Visits</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead>Page</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>IP</TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Location</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentVisits.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400">No visits recorded</TableCell></TableRow>
                  ) : (
                    recentVisits.map((v) => (
                      <TableRow key={v.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">{v.page || '/'}</TableCell>
                        <TableCell>{v.expand?.userId?.name || 'Anonymous'}</TableCell>
                        <TableCell className="font-mono text-xs">{v.ip || '—'}</TableCell>
                        <TableCell>{formatDateTime(v.timestamp)}</TableCell>
                        <TableCell>{v.location || '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="team" className="mt-6">
          <Card className="border-0 shadow-lg overflow-hidden">
            <CardHeader>
              <CardTitle>Team Activity & Online Duration</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead>Team Member</TableHead>
                    <TableHead>Total Online (min)</TableHead>
                    <TableHead>Last Seen</TableHead>
                    <TableHead>Recent Activity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teamActivity.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-400">No team activity recorded</TableCell></TableRow>
                  ) : (
                    teamActivity.map((t) => (
                      <TableRow key={t.userId} className="hover:bg-slate-50">
                        <TableCell className="font-medium">{t.user}</TableCell>
                        <TableCell>{t.totalDuration} min</TableCell>
                        <TableCell>{t.lastSeen ? formatDateTime(t.lastSeen) : '—'}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {t.events.slice(-3).map((ev, idx) => (
                              <Badge
                                key={idx}
                                variant={ev.type === 'login' ? 'default' : 'destructive'}
                                className="text-xs"
                              >
                                {ev.type} {format(new Date(ev.timestamp), 'hh:mm a')}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Analytics;